"use client"

import { useState, useEffect } from "react"
import { Bar, BarChart, Line, LineChart, Pie, PieChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, Cell } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2 } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { format } from "date-fns"
import { supabase } from "@/utils/supabase/client"
import { Expense, DailyTrend, ProductCategory } from "@/types"
import { useAuth } from "@/contexts/auth-context"

interface DashboardData {
  today: number;
  thisWeek: number;
  thisMonth: number;
  budget: { used: number; total: number };
  dailyTrend: DailyTrend[];
  byCategory: ProductCategory[];
  recentExpenses: Expense[];
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF"];

export function ExpenseDashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchData = async () => {
      if (!currentOrganization) return;
      setIsLoading(true)
      try {
        const { data, error } = await supabase.rpc('get_expense_dashboard_data', {
          org_id: currentOrganization.id
        });
        if (error) throw error;
        setData(data)
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Could not load dashboard data. " + error.message,
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [toast, currentOrganization])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!data) {
    return <p className="text-center text-muted-foreground mt-8">Could not load dashboard data.</p>
  }

  return (
    <div className="space-y-6 mt-6">
        {/* Stat Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card><CardHeader><CardTitle>Today's Expenses</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">Rs. {data.today.toLocaleString()}</div></CardContent></Card>
            <Card><CardHeader><CardTitle>This Week</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">Rs. {data.thisWeek.toLocaleString()}</div></CardContent></Card>
            <Card><CardHeader><CardTitle>This Month</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">Rs. {data.thisMonth.toLocaleString()}</div><p className="text-xs text-muted-foreground">Budget: Rs. {data.budget.total.toLocaleString()}</p></CardContent></Card>
            <Card><CardHeader><CardTitle>Pending Approvals</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">0</div></CardContent></Card>
        </div>

        {/* Charts */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            <Card className="lg:col-span-4">
                <CardHeader><CardTitle>Daily Expense Trend</CardTitle></CardHeader>
                <CardContent className="pl-2">
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data.dailyTrend}>
                            <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `Rs.${value/1000}k`} />
                            <Tooltip />
                            <Bar dataKey="total" fill="#8884d8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
            <Card className="lg:col-span-3">
                <CardHeader><CardTitle>Expenses by Category</CardTitle></CardHeader>
                <CardContent>
                     <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={data.byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8">
                                 {data.byCategory.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} /> )}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
        
        {/* Recent Expenses Table */}
        <Card>
            <CardHeader><CardTitle>Recent Expenses</CardTitle></CardHeader>
            <CardContent>
                <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {data.recentExpenses.length === 0 ? (
                            <TableRow><TableCell colSpan={4} className="h-24 text-center">No recent expenses.</TableCell></TableRow>
                        ) : (
                            data.recentExpenses.map((expense: any) => (
                                <TableRow key={expense.id}>
                                    <TableCell>{format(new Date(expense.expense_date), "PPP")}</TableCell>
                                    <TableCell className="font-medium">{expense.description}</TableCell>
                                    <TableCell>{expense.category_name || 'Uncategorized'}</TableCell>
                                    <TableCell className="text-right">Rs. {expense.amount.toLocaleString()}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    </div>
  )
} 